import {
  type ActiveAdvancedFilterState,
  type AdvancedFilterState,
} from '../../../../../../shared/types';

import { type EntitlementRisksRowType } from './types';

export const RISKS_TEMP_LIST_ITEMS_MOCK: EntitlementRisksRowType[] = [
  {
    id: 1,
    title: 'SAVB001 - Basis Development & System Administration 1',
    role: 'DevOpsSC',
    description:
      'SAVBS02 - Basis Development, SAVBS11 - System Administration 1',
    violations: 97,
    critical: 100,
    violationsEntitlement: 10,
    priority: 'high',
    riskDescription:
      'A developer could modify an existing program in production, perform traces to the program, and configure the production environment to run the program. This may affect system performance, data integrity and inappropriate program modification.',
    riskType: 'SOD',
    riskOwner: 'Wilson Dias (wdias)',
    ruleSet: 'SAP S4Hana Ruleset',
    businessProcess: 'P2P - Oracle EBS (MCPS)',
    application: 'SAP',
    conflicts: [
      'SAVBS02 - Basis Development',
      'SAVBS11 - System Administration',
    ],
  },
  {
    id: 2,
    title: 'FINS002 - Financial Posting & Payment Processing',
    role: 'FinanceClerk',
    description: 'FINS02 - Journal Entries, FINS05 - Payment Processing',
    violations: 85,
    critical: 92,
    violationsEntitlement: 8,
    priority: 'high',
    riskDescription:
      'A user could post journal entries and also process outgoing payments, potentially enabling fraudulent transactions or unauthorized disbursements.',
    riskType: 'SOD',
    riskOwner: 'Samantha Park (spark)',
    ruleSet: 'SAP S4Hana Ruleset',
    businessProcess: 'RTR - Oracle EBS (MCPS)',
    application: 'SAP',
    conflicts: ['FINS02 - Journal Entries', 'FINS05 - Payment Processing'],
  },
  {
    id: 3,
    title: 'PROC003 - Purchase Requisition & Vendor Management',
    role: 'ProcureMgr',
    description: 'PROC01 - Requisition Creation, PROC09 - Vendor Approval',
    violations: 72,
    critical: 89,
    violationsEntitlement: 6,
    priority: 'medium',
    riskDescription:
      'Combining vendor approval and requisition creation can lead to biased procurement or unauthorized vendor onboarding.',
    riskType: 'SOD',
    riskOwner: 'Liam Chen (lchen)',
    ruleSet: 'SAP S4Hana Ruleset',
    businessProcess: 'P2P - Oracle EBS (MCPS)',
    application: 'SAP',
    conflicts: ['PROC01 - Requisition Creation', 'PROC09 - Vendor Approval'],
  },
  {
    id: 4,
    title: 'HRM004 - Payroll Processing & Employee Master Data',
    role: 'HRSpecialist',
    description: 'HRM01 - Master Data Maintenance, HRM04 - Payroll',
    violations: 65,
    critical: 78,
    violationsEntitlement: 7,
    priority: 'high',
    riskDescription:
      'A user could manipulate employee master data and payroll calculations, leading to unauthorized salary changes.',
    riskType: 'SOD',
    riskOwner: 'Ava Martinez (amartinez)',
    ruleSet: 'SAP S4Hana Ruleset',
    businessProcess: 'HCM - Oracle EBS (MCPS)',
    application: 'SAP',
    conflicts: ['HRM01 - Master Data Maintenance', 'HRM04 - Payroll'],
  },
  {
    id: 5,
    title: 'SALES005 - Sales Order & Credit Limit Management',
    role: 'SalesExec',
    description: 'SALES01 - Sales Order Creation, SALES08 - Credit Control',
    violations: 48,
    critical: 63,
    violationsEntitlement: 4,
    priority: 'medium',
    riskDescription:
      'User can bypass credit checks by altering limits and creating orders, impacting financial risk exposure.',
    riskType: 'SOD',
    riskOwner: 'Noah Singh (nsingh)',
    ruleSet: 'SAP S4Hana Ruleset',
    businessProcess: 'OTC - Oracle EBS (MCPS)',
    application: 'SAP',
    conflicts: ['SALES01 - Sales Order Creation', 'SALES08 - Credit Control'],
  },
  {
    id: 6,
    title: 'ITSEC006 - User Access Provisioning & Audit Logging',
    role: 'SecurityAdmin',
    description: 'ITSEC02 - Role Assignment, ITSEC09 - Audit Log Control',
    violations: 98,
    critical: 100,
    violationsEntitlement: 9,
    priority: 'critical',
    riskDescription:
      'A user could provision themselves unauthorized access and suppress logs to hide activities.',
    riskType: 'SOD',
    riskOwner: 'Isabella Wong (iwong)',
    ruleSet: 'SAP GRC Ruleset',
    businessProcess: 'GRC - Compliance',
    application: 'SAP',
    conflicts: ['ITSEC02 - Role Assignment', 'ITSEC09 - Audit Log Control'],
  },
  {
    id: 7,
    title: 'WHM007 - Goods Receipt & Inventory Adjustment',
    role: 'WarehouseClerk',
    description: 'WHM01 - GRN Entry, WHM05 - Inventory Changes',
    violations: 55,
    critical: 60,
    violationsEntitlement: 5,
    priority: 'low',
    riskDescription:
      'Enabling users to both receive goods and adjust inventory may conceal theft or entry errors.',
    riskType: 'SOD',
    riskOwner: 'Lucas Petrov (lpetrov)',
    ruleSet: 'SAP S4Hana Ruleset',
    businessProcess: 'SCM - Oracle EBS (MCPS)',
    application: 'SAP',
    conflicts: ['WHM01 - GRN Entry', 'WHM05 - Inventory Changes'],
  },
  {
    id: 8,
    title: 'PRJ008 - Project Costing & Approval',
    role: 'ProjectLead',
    description: 'PRJ01 - Project Budgeting, PRJ06 - Approval of Spend',
    violations: 43,
    critical: 57,
    violationsEntitlement: 3,
    priority: 'medium',
    riskDescription:
      'Project leads could overspend or approve non-budgeted expenses, causing financial mismanagement.',
    riskType: 'SOD',
    riskOwner: 'Grace Tanaka (gtanaka)',
    ruleSet: 'SAP S4Hana Ruleset',
    businessProcess: 'Projects - Oracle EBS',
    application: 'SAP',
    conflicts: ['PRJ01 - Project Budgeting', 'PRJ06 - Approval of Spend'],
  },
  {
    id: 9,
    title: 'LOG009 - Logistics Planning & Vendor Payment',
    role: 'LogisticsMgr',
    description: 'LOG01 - Supply Planning, FIN05 - Vendor Payment',
    violations: 90,
    critical: 95,
    violationsEntitlement: 6,
    priority: 'high',
    riskDescription:
      'Combining logistics planning with payment authority may lead to fraudulent vendor favoritism.',
    riskType: 'SOD',
    riskOwner: 'Ethan Müller (emuller)',
    ruleSet: 'SAP S4Hana Ruleset',
    businessProcess: 'P2P - Oracle EBS (MCPS)',
    application: 'SAP',
    conflicts: ['LOG01 - Supply Planning', 'FIN05 - Vendor Payment'],
  },
  {
    id: 10,
    title: 'QA010 - Quality Inspection & Material Release',
    role: 'QualityInspector',
    description: 'QA01 - Inspection Results, QA04 - Release Control',
    violations: 41,
    critical: 54,
    violationsEntitlement: 4,
    priority: 'medium',
    riskDescription:
      'Inspector may pass failed goods or skip quality checks, risking defective deliveries.',
    riskType: 'SOD',
    riskOwner: 'Olivia Ahmed (oahmed)',
    ruleSet: 'SAP S4Hana Ruleset',
    businessProcess: 'Manufacturing - Oracle EBS',
    application: 'SAP',
    conflicts: ['QA01 - Inspection Results', 'QA04 - Release Control'],
  },
  {
    id: 11,
    title: 'CSTM011 - Customer Master & Credit Risk',
    role: 'CustomerAdmin',
    description: 'CSTM01 - Customer Master Entry, CSTM05 - Credit Setup',
    violations: 67,
    critical: 71,
    violationsEntitlement: 5,
    priority: 'medium',
    riskDescription:
      'A user could create fake customers and assign generous credit terms, leading to credit loss.',
    riskType: 'SOD',
    riskOwner: 'Mason Rivera (mrivera)',
    ruleSet: 'SAP S4Hana Ruleset',
    businessProcess: 'OTC - Oracle EBS (MCPS)',
    application: 'SAP',
    conflicts: ['CSTM01 - Customer Master Entry', 'CSTM05 - Credit Setup'],
  },
  {
    id: 12,
    title: 'BILL012 - Billing & Revenue Recognition',
    role: 'BillingClerk',
    description: 'BILL01 - Invoice Generation, BILL04 - Revenue Postings',
    violations: 58,
    critical: 70,
    violationsEntitlement: 4,
    priority: 'medium',
    riskDescription:
      'Improperly timed billing and revenue recognition may distort financial reporting.',
    riskType: 'SOD',
    riskOwner: 'Sophia Novak (snovak)',
    ruleSet: 'SAP S4Hana Ruleset',
    businessProcess: 'RTR - Oracle EBS (MCPS)',
    application: 'SAP',
    conflicts: ['BILL01 - Invoice Generation', 'BILL04 - Revenue Postings'],
  },
  {
    id: 13,
    title: 'BANK013 - Bank Master & Payment Runs',
    role: 'TreasuryOfficer',
    description: 'BANK02 - Bank Master Data, BANK06 - Payment Execution',
    violations: 83,
    critical: 90,
    violationsEntitlement: 6,
    priority: 'high',
    riskDescription:
      'Fraud risk due to ability to create fake banks and initiate payments to them.',
    riskType: 'SOD',
    riskOwner: 'Benjamin Lee (blee)',
    ruleSet: 'SAP S4Hana Ruleset',
    businessProcess: 'RTR - Oracle EBS (MCPS)',
    application: 'SAP',
    conflicts: ['BANK02 - Bank Master Data', 'BANK06 - Payment Execution'],
  },
  {
    id: 14,
    title: 'AUD014 - Access Reviews & Role Assignment',
    role: 'Auditor',
    description: 'AUD01 - Access Review Execution, ITSEC02 - Role Changes',
    violations: 74,
    critical: 82,
    violationsEntitlement: 7,
    priority: 'high',
    riskDescription:
      'Enables auditors to influence access review outcomes and directly assign roles, compromising independence.',
    riskType: 'SOD',
    riskOwner: 'Chloe Becker (cbecker)',
    ruleSet: 'SAP GRC Ruleset',
    businessProcess: 'GRC - Compliance',
    application: 'SAP',
    conflicts: ['AUD01 - Access Review Execution', 'ITSEC02 - Role Changes'],
  },
  {
    id: 15,
    title: 'AP015 - AP Invoice & Payment Authorization',
    role: 'APSpecialist',
    description: 'AP01 - Invoice Entry, AP04 - Payment Authorization',
    violations: 92,
    critical: 97,
    violationsEntitlement: 8,
    priority: 'critical',
    riskDescription:
      'May lead to self-authorized payments and misuse of funds without approval layers.',
    riskType: 'SOD',
    riskOwner: 'Henry Zhao (hzhao)',
    ruleSet: 'SAP S4Hana Ruleset',
    businessProcess: 'P2P - Oracle EBS (MCPS)',
    application: 'SAP',
    conflicts: ['AP01 - Invoice Entry', 'AP04 - Payment Authorization'],
  },
];

export const RISKS_FILTERS: AdvancedFilterState[] = [
  {
    value: 'risk',
    label: 'Risk',
    isChecked: true,
    method: 'CUSTOM',
    options: [],
  },
  {
    value: 'riskScore',
    label: 'Risk Score',
    isChecked: true,
    method: 'CUSTOM',
    options: [],
  },
  {
    value: 'riskPriority',
    label: 'Risk Priority',
    isChecked: true,
    method: 'CUSTOM',
    options: [],
  },
  {
    value: 'riskType',
    label: 'Risk Type',
    isChecked: true,
    method: 'CUSTOM',
    options: [],
  },
  {
    value: 'ruleset',
    label: 'Ruleset',
    isChecked: true,
    method: 'CUSTOM',
    options: [],
  },
  {
    value: 'functions',
    label: 'Functions',
    isChecked: true,
    method: 'CUSTOM',
    options: [],
  },
  {
    value: 'businessProcess',
    label: 'Business Process',
    isChecked: true,
    method: 'CUSTOM',
    options: [],
  },
];

export const ACTIVE_RISKS_FILTERS: ActiveAdvancedFilterState[] = [
  {
    value: 'risk',
    method: 'CUSTOM',
    options: [],
  },
  {
    value: 'riskScore',
    method: 'CUSTOM',
    options: [],
  },
  {
    value: 'riskPriority',
    method: 'CUSTOM',
    options: [],
  },
  {
    value: 'riskType',
    method: 'CUSTOM',
    options: [],
  },
  {
    value: 'ruleset',
    method: 'CUSTOM',
    options: [],
  },
  {
    value: 'functions',
    method: 'CUSTOM',
    options: [],
  },
  {
    value: 'businessProcess',
    method: 'CUSTOM',
    options: [],
  },
];
